import {
  Maximize,
  FileImage,
  RefreshCw,
  Crop,
  Stamp,
  Info,
  RotateCw,
  PenTool,
  FileText,
  Shield,
  Sparkles,
  Wand2,
  PlayCircle,
} from 'lucide-react';
import type { Tool } from '@/registry/types';
import { ImageResize } from './ImageResize';
import { ImageCompress } from './ImageCompress';
import { ImageConvert } from './ImageConvert';
import { ImageCrop } from './ImageCrop';
import { ImageWatermark } from './ImageWatermark';
import { ImageMetadata } from './ImageMetadata';
import { ImageRotate } from './ImageRotate';
import { ImageAnnotator } from './ImageAnnotator';
import { ImageBulkRename } from './ImageBulkRename';
import { ExifScrub } from './ExifScrub';
import { ImageUpscale } from './ImageUpscale';
import { GifMaker } from './GifMaker';
import { BackgroundRemover } from './BackgroundRemover';

export const imageTools: Tool[] = [
  {
    id: 'image-resize',
    name: 'Image resize',
    category: 'images',
    description: 'Resize with social presets and aspect-ratio lock.',
    keywords: ['resize', 'image', 'photo', 'scale', 'social', 'linkedin', 'twitter', 'instagram'],
    icon: Maximize,
    component: ImageResize,
    featured: true,
    accepts: 'image',
    inBrowser: true,
  },
  {
    id: 'image-compress',
    name: 'Image compress',
    category: 'images',
    description: 'Reduce image file size with quality control.',
    keywords: ['compress', 'optimize', 'smaller', 'shrink', 'jpeg', 'webp'],
    icon: FileImage,
    component: ImageCompress,
    featured: true,
    accepts: 'image',
    inBrowser: true,
  },
  {
    id: 'background-remover',
    name: 'Background remover',
    category: 'images',
    description: 'ML-powered local background removal. First use downloads a model.',
    keywords: ['background', 'remove', 'cutout', 'transparent', 'subject', 'bg'],
    icon: Wand2,
    component: BackgroundRemover,
    featured: true,
    accepts: 'image',
    badge: 'new',
    inBrowser: true,
  },
  {
    id: 'image-convert',
    name: 'Image format converter',
    category: 'images',
    description: 'Convert PNG ↔ JPEG ↔ WebP. Batch supported.',
    keywords: ['convert', 'png', 'jpg', 'jpeg', 'webp', 'format'],
    icon: RefreshCw,
    component: ImageConvert,
    accepts: 'image',
    inBrowser: true,
  },
  {
    id: 'image-annotator',
    name: 'Image annotator',
    category: 'images',
    description: 'Mark up screenshots with shapes, arrows, highlights, and text.',
    keywords: ['annotate', 'screenshot', 'arrow', 'highlight', 'mark', 'draw'],
    icon: PenTool,
    component: ImageAnnotator,
    accepts: 'image',
    inBrowser: true,
  },
  {
    id: 'image-crop',
    name: 'Image crop',
    category: 'images',
    description: 'Crop free-form or to fixed aspect ratios.',
    keywords: ['crop', 'trim', 'cut', 'square'],
    icon: Crop,
    component: ImageCrop,
    accepts: 'image',
    inBrowser: true,
  },
  {
    id: 'image-watermark',
    name: 'Image watermark',
    category: 'images',
    description: 'Stamp text watermarks with color, opacity, position.',
    keywords: ['watermark', 'stamp', 'brand', 'protect'],
    icon: Stamp,
    component: ImageWatermark,
    accepts: 'image',
    inBrowser: true,
  },
  {
    id: 'image-upscale',
    name: 'Image upscaler',
    category: 'images',
    description: 'Multi-step Lanczos-like upscaling for sharper enlargements.',
    keywords: ['upscale', 'enlarge', 'enhance', 'super resolution'],
    icon: Sparkles,
    component: ImageUpscale,
    accepts: 'image',
    inBrowser: true,
  },
  {
    id: 'gif-maker',
    name: 'GIF maker',
    category: 'images',
    description: 'Combine images into an animated GIF with adjustable frame rate.',
    keywords: ['gif', 'animation', 'animated', 'frames', 'loop'],
    icon: PlayCircle,
    component: GifMaker,
    accepts: 'image',
    badge: 'new',
    inBrowser: true,
  },
  {
    id: 'image-bulk-rename',
    name: 'Bulk image rename',
    category: 'images',
    description: 'Rename hundreds of files with a token pattern. ZIP output.',
    keywords: ['rename', 'bulk', 'batch', 'pattern', 'zip'],
    icon: FileText,
    component: ImageBulkRename,
    accepts: 'image',
    inBrowser: true,
  },
  {
    id: 'exif-scrub',
    name: 'EXIF scrubber',
    category: 'images',
    description: 'Inspect every metadata tag. Strip EXIF and GPS for safe sharing.',
    keywords: ['exif', 'metadata', 'gps', 'privacy', 'strip', 'scrub'],
    icon: Shield,
    component: ExifScrub,
    accepts: 'image',
    inBrowser: true,
  },
  {
    id: 'image-metadata',
    name: 'Image inspector',
    category: 'images',
    description: 'Inspect dimensions, type, size, and dominant colors.',
    keywords: ['metadata', 'inspect', 'dimensions', 'colors', 'palette'],
    icon: Info,
    component: ImageMetadata,
    accepts: 'image',
    inBrowser: true,
  },
  {
    id: 'image-rotate',
    name: 'Rotate & flip',
    category: 'images',
    description: 'Rotate by any angle, flip horizontally or vertically.',
    keywords: ['rotate', 'flip', 'mirror', 'transform'],
    icon: RotateCw,
    component: ImageRotate,
    accepts: 'image',
    inBrowser: true,
  },
];
